import React, { useState } from 'react';
import { 
  X, ChevronRight, ChevronDown, Home, Plus, Utensils, TrendingUp, 
  Activity, Dumbbell, Calendar, Award, MessageCircle, Leaf, Watch, 
  Droplets, Users, FileText, Crown, LogOut, Settings, Shield, HelpCircle, Minus, BarChart3, Video, Share2, Pill, Target, Sparkles, HeartPulse
} from 'lucide-react';
import DarkModeToggle from './DarkModeToggle';
import { useTranslation } from 'react-i18next';

const SideNavigation = ({ 
  isOpen, 
  onClose, 
  user, 
  isPremium, 
  currentView, 
  onNavigate, 
  onLogout 
}) => {
  const [expandedMenus, setExpandedMenus] = useState({});
  const [showLegalModal, setShowLegalModal] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const { t } = useTranslation();

  // FAQ Data
  const faqItems = [
    {
      question: "How does the AI food recognition work?",
      answer: "Simply take a photo of your meal, and our advanced AI analyzes it in seconds. It identifies individual foods, estimates portion sizes, and calculates calories, protein, carbs, and fats."
    },
    {
      question: "Is NutriFitness AI free to use?",
      answer: "Yes! NutriFitness AI offers a free plan with 5 AI food scans per day, basic meal logging, and exercise tracking. For unlimited features, upgrade to Premium."
    },
    {
      question: "How accurate is the calorie counting?",
      answer: "Our AI provides calorie estimates within 5-10% accuracy for most common foods. For packaged foods, you can scan barcodes for exact nutritional information."
    },
    {
      question: "Can I track my exercises and workouts?",
      answer: "Absolutely! NutriFitness AI includes a comprehensive exercise tracker with 200+ exercises, custom workout creation, and AI exercise coaching."
    },
    {
      question: "What is the AI Exercise Coach?",
      answer: "Our AI Exercise Coach is your personal virtual trainer! It creates personalized workout plans based on your fitness level, goals, and available equipment."
    },
    {
      question: "How do I find healthy recipes?",
      answer: "Our Recipe Hub has thousands of healthy recipes filtered by your dietary preferences, calorie goals, and ingredients. Find breakfast, lunch, dinner, and snack ideas."
    },
    {
      question: "Can I cancel my Premium subscription?",
      answer: "You can cancel your Premium subscription anytime from your account settings. There are no cancellation fees."
    },
    {
      question: "Is my data private and secure?",
      answer: "Your privacy is our priority. All data is encrypted with 256-bit SSL, and we never sell your personal information."
    },
    {
      question: "Can I schedule live coaching sessions?",
      answer: "Yes! NutriFitness AI offers a booking system for live coaching sessions with certified fitness and nutrition professionals."
    },
    {
      question: "Can I connect my fitness wearable?",
      answer: "NutriFitness AI integrates with Fitbit, Apple Health, Google Fit, and other popular fitness platforms for automatic syncing."
    }
  ];

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const handleNavigate = (viewId) => {
    onNavigate(viewId);
    onClose();
  };

  // Menu structure with categories and sub-items
  const menuItems = [
    {
      id: 'my-plan',
      label: 'MY PLAN',
      icon: Sparkles,
      isExpandable: false
    },
    {
      id: 'exercise-group',
      label: t('nav.exercises').toUpperCase(),
      icon: Activity,
      isExpandable: true,
      subItems: [
        { id: 'exercise-library', label: t('exercises.title'), icon: Dumbbell },
        { id: 'workout-wizard', label: t('nav.workoutWizard'), icon: Activity },
        { id: 'session-logger', label: t('nav.sessionLogger'), icon: Activity },
        { id: 'exercise', label: t('nav.tracker'), icon: Activity },
        { id: 'exercise-coach', label: t('nav.aiCoach'), icon: MessageCircle },
        { id: 'workout-planner', label: t('nav.planner'), icon: Calendar },
        { id: 'live-coaching', label: t('liveCoach.title'), icon: Video },
      ]
    },
    {
      id: 'meal-group',
      label: t('dashboard.meals').toUpperCase(),
      icon: Utensils,
      isExpandable: true,
      subItems: [
        { id: 'capture', label: t('scanner.logMeal'), icon: Plus },
        { id: 'history', label: t('session.history'), icon: Utensils },
        { id: 'analytics', label: t('nav.analytics'), icon: TrendingUp },
        { id: 'micronutrients', label: t('micro.title'), icon: Pill },
        { id: 'adaptive-target', label: t('adaptive.title'), icon: Target },
        { id: 'recommendations', label: t('nav.whatToEat'), icon: TrendingUp },
        { id: 'ai-meal-planner', label: t('nav.mealPlanner'), icon: Calendar },
      ]
    },
    {
      id: 'badges',
      label: t('dashboard.achievements').toUpperCase(),
      icon: Award,
      isExpandable: false
    },
    {
      id: 'ai-chat',
      label: t('nav.aiNutri').toUpperCase(),
      icon: MessageCircle,
      isExpandable: false
    },
    {
      id: 'diet-group',
      label: t('nav.recipes').toUpperCase(),
      icon: Leaf,
      isExpandable: true,
      subItems: [
        { id: 'diet-recipes', label: t('nav.organic'), icon: Leaf, filter: 'organic' },
        { id: 'diet-recipes', label: t('nav.vegan'), icon: Leaf, filter: 'vegan' },
        { id: 'diet-recipes', label: t('nav.glutenFree'), icon: Leaf, filter: 'gluten-free' },
        { id: 'diet-recipes', label: t('nav.heartHealthy'), icon: Leaf, filter: 'heart-healthy' },
      ]
    },
    {
      id: 'wearables',
      label: t('nav.wearables').toUpperCase(),
      icon: Watch,
      isExpandable: false
    },
    {
      id: 'premium',
      label: t('water.tracker').toUpperCase(),
      icon: Droplets,
      isExpandable: false
    },
    {
      id: 'referral',
      label: t('nav.referral').toUpperCase(),
      icon: Users,
      isExpandable: false
    },
    {
      id: 'video-studio',
      label: t('nav.videoStudio').toUpperCase(),
      icon: Video,
      isExpandable: false
    },
    {
      id: 'social',
      label: t('nav.share').toUpperCase(),
      icon: Share2,
      isExpandable: false
    },
    {
      id: 'blog',
      label: t('nav.blog').toUpperCase(),
      icon: FileText,
      isExpandable: false
    },
    {
      id: 'how-it-works',
      label: t('nav.howItWorks').toUpperCase(),
      icon: HelpCircle,
      isExpandable: false
    },
    {
      id: 'account',
      label: t('nav.account').toUpperCase(),
      icon: Settings,
      isExpandable: false
    },
    {
      id: 'admin-funnel',
      label: 'ADMIN · FUNNEL',
      icon: TrendingUp,
      isExpandable: false,
      adminOnly: true,
    },
  ];

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50"
      onClick={onClose}
    >
      <div 
        className="w-80 h-full bg-white dark:bg-gray-900 shadow-xl overflow-y-auto transition-colors duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Menu Header - Consistent black background */}
        <div className="bg-black p-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-v4.png" alt="Logo" className="h-16 w-16 object-contain" />
          </div>
          <button
            onClick={onClose}
            className="text-white p-2 hover:bg-gray-800 rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <X size={24} />
          </button>
        </div>
        
        {/* User Info & Points */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 dark:bg-gray-800">
          <p className="font-bold text-gray-800 dark:text-white">{t('nav.greeting')}, {user?.name?.split(' ')[0] || t('nav.defaultUser')}</p>
          <p className="text-sm text-[#FF6600] font-semibold mt-1">
            {t('nav.points')}: {user?.points?.toLocaleString() || '0'}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <DarkModeToggle variant="minimal" />
            <span className="text-xs text-gray-500 dark:text-gray-400">{t('nav.darkMode')}</span>
          </div>
        </div>
        
        {/* Menu Items */}
        <nav className="py-2">
          {/* Home - Always at top */}
          <button
            onClick={() => handleNavigate('home')}
            className={`w-full flex items-center justify-between px-4 py-4 text-left transition-colors border-b border-gray-100 dark:border-gray-800 ${
              currentView === 'home' 
                ? 'bg-orange-50 dark:bg-orange-900/20 text-[#FF6600]' 
                : 'text-gray-700 dark:text-gray-300 dark:text-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-800'
            }`}
          >
            <div className="flex items-center gap-3">
              <Home size={20} />
              <span className="font-bold text-sm tracking-wide">{t('nav.home').toUpperCase()}</span>
            </div>
          </button>

          {menuItems.filter(item => !item.adminOnly || user?.is_admin).map((item) => (
            <div key={item.id} className="border-b border-gray-100 dark:border-gray-800">
              {item.isExpandable ? (
                <>
                  {/* Expandable Menu Header */}
                  <button
                    onClick={() => toggleMenu(item.id)}
                    className={`w-full flex items-center justify-between px-4 py-4 text-left transition-colors ${
                      expandedMenus[item.id] 
                        ? 'bg-gray-50 dark:bg-gray-900 dark:bg-gray-800' 
                        : 'hover:bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon size={20} className="text-[#FF6600]" />
                      <span className="font-bold text-sm tracking-wide text-gray-700 dark:text-gray-300 dark:text-gray-200">
                        {item.label}
                      </span>
                    </div>
                    {expandedMenus[item.id] ? (
                      <ChevronDown size={20} className="text-gray-400" />
                    ) : (
                      <ChevronRight size={20} className="text-gray-400" />
                    )}
                  </button>
                  
                  {/* Sub-items */}
                  {expandedMenus[item.id] && (
                    <div className="bg-gray-50 dark:bg-gray-900 dark:bg-gray-800/50 py-1">
                      {item.subItems.map((subItem, idx) => (
                        <button
                          key={`${subItem.id}-${idx}`}
                          onClick={() => handleNavigate(subItem.id)}
                          className={`w-full flex items-center gap-3 pl-12 pr-4 py-3 text-left transition-colors ${
                            currentView === subItem.id 
                              ? 'bg-orange-100 dark:bg-orange-900/30 text-[#FF6600]' 
                              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <span className="text-sm font-medium">{subItem.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                /* Non-expandable Menu Item */
                <button
                  onClick={() => handleNavigate(item.id)}
                  className={`w-full flex items-center justify-between px-4 py-4 text-left transition-colors ${
                    currentView === item.id 
                      ? 'bg-orange-50 dark:bg-orange-900/20 text-[#FF6600]' 
                      : 'text-gray-700 dark:text-gray-300 dark:text-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-800'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <item.icon size={20} className={currentView === item.id ? 'text-[#FF6600]' : 'text-[#FF6600]'} />
                    <span className="font-bold text-sm tracking-wide">{item.label}</span>
                  </div>
                  <ChevronRight size={20} className="text-gray-400" />
                </button>
              )}
            </div>
          ))}

          {/* Premium Subscription */}
          {!isPremium && (
            <button
              onClick={() => handleNavigate('subscription')}
              className="w-full flex items-center justify-between px-4 py-4 text-left transition-colors border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-800"
            >
              <div className="flex items-center gap-3">
                <Crown size={20} className="text-yellow-500" />
                <span className="font-bold text-sm tracking-wide text-gray-700 dark:text-gray-300 dark:text-gray-200">{t('nav.premium').toUpperCase()}</span>
              </div>
              <ChevronRight size={20} className="text-gray-400" />
            </button>
          )}
        </nav>

        {/* Legal Links */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-center gap-2 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
            <button 
              onClick={() => setShowLegalModal('faq')}
              className="hover:text-[#FF6600] transition-colors"
            >
              {t('legal.faq')}
            </button>
            <span>|</span>
            <button 
              onClick={() => setShowLegalModal('privacy')}
              className="hover:text-[#FF6600] transition-colors"
            >
              {t('legal.privacy')}
            </button>
            <span>|</span>
            <button 
              onClick={() => setShowLegalModal('terms')}
              className="hover:text-[#FF6600] transition-colors"
            >
              {t('legal.terms')}
            </button>
            <span>|</span>
            <a 
              href="mailto:support@nutrifitness.us"
              className="hover:text-[#FF6600] transition-colors"
            >
              {t('legal.contact')}
            </a>
          </div>
        </div>

        {/* Logout Button at Bottom */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 mt-auto">
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 py-3 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors font-semibold"
          >
            <LogOut size={20} />
            <span>{t('nav.logout')}</span>
          </button>
        </div>
      </div>

      {/* Legal Modal */}
      {showLegalModal && (
        <div className="fixed inset-0 bg-black/50 z-[10001] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-black p-4 flex items-center justify-between">
              <div className="flex items-center gap-3 text-white">
                {showLegalModal === 'privacy' ? (
                  <Shield size={24} className="text-[#FF6600]" />
                ) : showLegalModal === 'faq' ? (
                  <HelpCircle size={24} className="text-[#FF6600]" />
                ) : (
                  <FileText size={24} className="text-[#FF6600]" />
                )}
                <h2 className="font-bold text-lg">
                  {showLegalModal === 'privacy' ? t('legal.privacyPolicy') : showLegalModal === 'faq' ? t('legal.faqTitle') : t('legal.termsOfService')}
                </h2>
              </div>
              <button 
                onClick={() => { setShowLegalModal(null); setExpandedFaq(null); }}
                className="text-white hover:text-[#FF6600] transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-4 sm:p-6 overflow-y-auto flex-1">
              {showLegalModal === 'faq' ? (
                <div className="space-y-3">
                  {faqItems.map((faq, index) => (
                    <div 
                      key={index}
                      className="bg-gray-50 dark:bg-gray-900 dark:bg-gray-800 rounded-xl overflow-hidden"
                    >
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                        className="w-full flex items-center justify-between p-4 text-left"
                      >
                        <span className="font-medium text-gray-900 dark:text-white pr-3 text-sm">
                          {faq.question}
                        </span>
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                          expandedFaq === index ? 'bg-[#FF6600] text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}>
                          {expandedFaq === index ? <Minus size={14} /> : <Plus size={14} />}
                        </div>
                      </button>
                      
                      {expandedFaq === index && (
                        <div className="px-4 pb-4">
                          <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Contact Support */}
                  <div className="mt-6 text-center p-4 bg-gradient-to-r from-[#FF6600] to-[#FF8533] rounded-xl">
                    <p className="text-white text-sm mb-3">{t('legal.stillHaveQuestions')}</p>
                    <a 
                      href="mailto:support@nutrifitness.us"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-[#FF6600] font-semibold rounded-full text-sm hover:shadow-lg transition-all"
                    >
                      <MessageCircle size={16} />
                      {t('legal.contactSupport')}
                    </a>
                  </div>
                </div>
              ) : showLegalModal === 'privacy' ? (
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <p className="text-gray-500 dark:text-gray-400 mb-4">Last updated: March 24, 2026</p>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">1. Introduction</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">Welcome to NutriFitness AI ("NutriFitness AI", "we", "us", or "our"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website.</p>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">2. Information We Collect</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-2"><strong>Personal Information:</strong></p>
                  <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 mb-4">
                    <li>Name and email address</li>
                    <li>Account credentials</li>
                    <li>Profile information (height, weight, age, gender, fitness goals)</li>
                    <li>Payment information (processed securely via Stripe)</li>
                  </ul>
                  <p className="text-gray-600 dark:text-gray-300 mb-2"><strong>Health & Fitness Data:</strong></p>
                  <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 mb-4">
                    <li>Food photos and meal logs</li>
                    <li>Nutritional data and calorie information</li>
                    <li>Progress photos and body measurements</li>
                    <li>Exercise and workout data</li>
                    <li>Water intake and hydration logs</li>
                    <li>Wearable device data (if connected)</li>
                  </ul>
                  <p className="text-gray-600 dark:text-gray-300 mb-2"><strong>Automatically Collected:</strong></p>
                  <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 mb-4">
                    <li>Device information and identifiers</li>
                    <li>Usage data and app interactions</li>
                    <li>IP address and location data</li>
                  </ul>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">3. How We Use Your Information</h3>
                  <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 mb-4">
                    <li>Provide and maintain our services</li>
                    <li>Analyze food photos using AI to provide nutritional information</li>
                    <li>Track your fitness progress and provide personalized recommendations</li>
                    <li>Process payments and manage subscriptions</li>
                    <li>Send you updates, notifications, and marketing communications (with consent)</li>
                    <li>Improve our services and develop new features</li>
                    <li>Ensure security and prevent fraud</li>
                  </ul>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">4. AI and Data Processing</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">NutriFitness AI uses artificial intelligence to analyze food photos and provide nutritional estimates. Your food images are processed by secure AI services to identify food items and estimate calorie content. We do not use your personal photos for AI training without explicit consent.</p>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">5. Data Sharing</h3>
                  <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 mb-4">
                    <li><strong>Service Providers:</strong> Third-party vendors (cloud hosting, payment processing, AI services)</li>
                    <li><strong>Connected Services:</strong> Wearable device integrations as necessary</li>
                    <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                  </ul>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">We do not sell your personal information to third parties.</p>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">6. Data Security</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">We implement appropriate technical and organizational security measures including encryption, secure servers, and access controls.</p>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">7. Your Rights</h3>
                  <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 mb-4">
                    <li>Access, correct, or delete your personal data</li>
                    <li>Export your data</li>
                    <li>Opt-out of marketing communications</li>
                    <li>Withdraw consent</li>
                  </ul>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">To exercise these rights, contact us at support@nutrifitness.us</p>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">8. Children's Privacy</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">Our services are not intended for children under 13. We do not knowingly collect personal information from children under 13.</p>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">9. Contact Us</h3>
                  <p className="text-gray-600 dark:text-gray-300">Email: support@nutrifitness.us<br/>Website: nutrifitness.us</p>
                </div>
              ) : (
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <p className="text-gray-500 dark:text-gray-400 mb-4">Last updated: March 24, 2026</p>
                  
                  {/* HEALTH DISCLAIMER BANNER */}
                  <div className="bg-red-50 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 rounded-xl p-4 mb-6">
                    <p className="text-red-700 dark:text-red-300 font-semibold text-sm">IMPORTANT: NutriFitness AI is NOT a medical service, licensed healthcare provider, certified personal trainer, or registered dietitian. All information is for general informational and educational purposes ONLY. You assume ALL RISKS associated with following any guidance provided through this platform.</p>
                  </div>

                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">1. Acceptance of Terms</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">By accessing or using NutriFitness AI, you agree to be bound by these Terms of Service, the Privacy Policy, and the Liability Waiver. <strong>These Terms remain in full force for the duration of your active subscription and for two (2) years following your last payment date.</strong></p>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">2. Service Description</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">NutriFitness AI provides AI-powered nutrition tracking, meal logging, exercise tracking, and personalized health recommendations. <strong>ALL features are provided for informational and educational purposes ONLY and do NOT constitute professional medical, nutritional, or fitness advice.</strong></p>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">3. Medical and Health Disclaimer</h3>
                  <div className="bg-amber-50 dark:bg-amber-900/30 border-l-4 border-amber-500 p-3 mb-4">
                    <p className="text-amber-800 dark:text-amber-300 font-semibold text-xs">CRITICAL NOTICE:</p>
                  </div>
                  <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 mb-4 space-y-2">
                    <li><strong>Not Medical Advice:</strong> Content is generated by AI and is NOT reviewed by licensed healthcare professionals.</li>
                    <li><strong>Consult Professionals:</strong> You MUST consult a qualified physician before starting any diet or exercise program.</li>
                    <li><strong>AI Limitations:</strong> Calorie counts and recommendations are APPROXIMATIONS and may contain errors.</li>
                    <li><strong>Individual Results Vary:</strong> No guarantees regarding weight loss, muscle gain, or health outcomes.</li>
                    <li><strong>Allergies:</strong> AI-generated meal plans may contain allergens. It is YOUR responsibility to verify all ingredients.</li>
                  </ul>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">4. Exercise & Physical Activity Disclaimer</h3>
                  <div className="bg-red-50 dark:bg-red-900/30 border-l-4 border-red-500 p-3 mb-4">
                    <p className="text-red-800 dark:text-red-300 font-semibold text-xs">WARNING — RISK OF PHYSICAL INJURY:</p>
                  </div>
                  <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 mb-4 space-y-2">
                    <li><strong>Inherent Risks:</strong> Physical exercise carries inherent risks of injury including strains, fractures, and cardiovascular events. You voluntarily assume ALL such risks.</li>
                    <li><strong>Exercise Demonstrations:</strong> Videos and instructions are for general informational purposes only, NOT personalized to your condition.</li>
                    <li><strong>Proper Form:</strong> It is YOUR responsibility to learn proper form from a certified professional.</li>
                    <li><strong>Self-Assessment:</strong> You are solely responsible for assessing your fitness level. Stop immediately if you experience pain or discomfort.</li>
                    <li><strong>Third-Party Content:</strong> Exercise videos may be sourced from third parties. NutriFitness AI does not guarantee their safety.</li>
                  </ul>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">5. Nutrition & Dietary Disclaimer</h3>
                  <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 mb-4 space-y-2">
                    <li><strong>Not Dietitian Advice:</strong> Recommendations are generated by AI, NOT registered dietitians.</li>
                    <li><strong>Calorie Accuracy:</strong> AI-estimated values are APPROXIMATE and may differ from actual values.</li>
                    <li><strong>Food Allergies:</strong> The Service does NOT guarantee identification of all allergens.</li>
                    <li><strong>Medical Conditions:</strong> If you have diabetes, eating disorders, or any condition affected by diet, consult your physician first.</li>
                  </ul>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">6. Assumption of Risk & Liability Waiver</h3>
                  <div className="bg-gray-100 dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl p-4 mb-4">
                    <p className="text-gray-800 dark:text-gray-100 dark:text-gray-200 font-semibold text-xs uppercase mb-2">BY USING THIS SERVICE, YOU VOLUNTARILY ASSUME ALL RISKS AND AGREE TO:</p>
                    <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 text-sm space-y-2">
                      <li>You voluntarily choose to participate in exercise routines and follow meal plans at your OWN RISK.</li>
                      <li>You RELEASE, WAIVE, AND AGREE NOT TO SUE NUTRI FITNESS, its officers, directors, employees, agents, and affiliates from any liability, claims, or causes of action arising from your use of the Service.</li>
                      <li>This waiver applies regardless of whether loss, damage, or injury is caused by negligence.</li>
                      <li>This assumption of risk shall be binding upon you, your heirs, executors, and assigns.</li>
                    </ul>
                  </div>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">7. Indemnification</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">You agree to indemnify, defend, and hold harmless NutriFitness AI from all claims, losses, expenses, and damages resulting from your use of the Service, any violation of these Terms, or any injury arising from exercise routines, dietary information, or other content.</p>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">8. Duration & Survival of Terms</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-2"><strong>These Terms remain in full force:</strong></p>
                  <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 mb-4">
                    <li>For the entire duration of your active subscription</li>
                    <li>For <strong>two (2) years</strong> following the date of your last payment</li>
                    <li>For <strong>two (2) years</strong> following account termination, whichever is later</li>
                  </ul>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">The liability waiver, assumption of risk, indemnification, and limitation of liability clauses survive termination.</p>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">9. Subscription & Payments</h3>
                  <ul className="list-disc pl-6 text-gray-600 dark:text-gray-300 mb-4">
                    <li>Subscriptions billed in advance (monthly or yearly) via Stripe</li>
                    <li>Cancel any time; takes effect at end of billing period</li>
                    <li><strong>Cancellation does NOT terminate these Terms</strong> — protective clauses remain for 2 years after last payment</li>
                  </ul>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">10. Limitation of Liability</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">TO THE MAXIMUM EXTENT PERMITTED BY LAW, NUTRI FITNESS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES INCLUDING: physical injury or death from exercise/dietary changes, inaccurate calorie counts, allergic reactions, or any damages from reliance on the Service. Total liability shall not exceed the amount paid in the 12 months preceding the claim.</p>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">11. Dispute Resolution</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">Any disputes shall be resolved through binding arbitration. You waive the right to a jury trial and class action participation.</p>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">12. Governing Law</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">These Terms are governed by the laws of the State of Florida, United States.</p>
                  
                  <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">13. Contact Us</h3>
                  <p className="text-gray-600 dark:text-gray-300">Email: support@nutrifitness.us<br/>Website: nutrifitness.us</p>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 dark:bg-gray-800">
              <button 
                onClick={() => { setShowLegalModal(null); setExpandedFaq(null); }}
                className="w-full bg-[#FF6600] hover:bg-[#E55C00] text-white py-3 rounded-xl font-semibold transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SideNavigation;
